import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Sparkles, ArrowRight, ChevronDown, ChevronUp, MessageSquare, Calendar, BookOpen,
  Search, Star, Users, Heart, Send, Loader2, Check, Activity, Zap, Moon, Brain,
  Dumbbell, Target, Shield, Flame
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'weight-loss', label: 'Weight Loss', icon: Flame },
  { id: 'energy', label: 'Energy', icon: Zap },
  { id: 'sleep', label: 'Sleep', icon: Moon },
  { id: 'gut', label: 'Gut Health', icon: Shield },
  { id: 'recovery', label: 'Recovery', icon: Activity },
  { id: 'joints', label: 'Joint Pain', icon: Activity },
  { id: 'brainfog', label: 'Brain Fog', icon: Brain },
  { id: 'hormones', label: 'Hormones', icon: Heart },
  { id: 'libido', label: 'Libido', icon: Heart },
  { id: 'aging', label: 'Healthy Aging', icon: Shield },
  { id: 'bodycomp', label: 'Body Composition', icon: Dumbbell },
  { id: 'digestion', label: 'Digestive Health', icon: Shield },
];

const SYMPTOMS = [
  { id: 'plateau', label: 'Weight Loss Plateau', causes: ['Metabolic adaptation', 'Hormonal shifts', 'Insufficient protein', 'Poor sleep quality'], goals: ['Break through the stall', 'Optimize medication response', 'Improve body composition'], path: 'Plateau Breaker optimization path' },
  { id: 'low-energy', label: 'Low Energy', causes: ['Declining NAD+ levels', 'Mitochondrial dysfunction', 'Poor sleep quality', 'Nutrient gaps'], goals: ['Sustained energy without stimulants', 'Mental clarity', 'Better recovery'], path: 'Energy Upgrade optimization path' },
  { id: 'poor-sleep', label: 'Poor Sleep', causes: ['Declining growth hormone', 'Stress and cortisol', 'Disrupted sleep architecture', 'Hormonal changes'], goals: ['Deeper restorative sleep', 'Better recovery', 'Improved energy'], path: 'Sleep Recovery optimization path' },
  { id: 'menopause', label: 'Menopause Symptoms', causes: ['Declining estrogen and progesterone', 'Metabolic changes', 'Sleep disruption', 'Mood shifts'], goals: ['Hormone balance', 'Energy restoration', 'Weight management'], path: "Women's Optimization path" },
  { id: 'brain-fog', label: 'Brain Fog', causes: ['Mitochondrial dysfunction', 'Inflammation', 'Poor sleep', 'Nutrient deficiency'], goals: ['Mental clarity', 'Sustained focus', 'Cognitive performance'], path: 'Energy Upgrade optimization path' },
  { id: 'joint-pain', label: 'Joint Pain', causes: ['Connective tissue wear', 'Inflammation', 'Increased activity after weight loss', 'Old injuries'], goals: ['Joint comfort', 'Better mobility', 'Sustained activity'], path: 'Recovery optimization path' },
  { id: 'gut-issues', label: 'Gut Issues', causes: ['Gut lining disruption', 'Medication side effects', 'Stress', 'Diet changes'], goals: ['Digestive comfort', 'Gut health support', 'Better nutrient absorption'], path: 'Recovery optimization path' },
  { id: 'belly-fat', label: 'Belly Fat', causes: ['Visceral fat accumulation', 'Hormonal shifts', 'Insulin resistance', 'Age-related GH decline'], goals: ['Targeted midsection improvement', 'Better body composition', 'Metabolic health'], path: 'Plateau Breaker optimization path' },
  { id: 'hair-loss', label: 'Hair Loss', causes: ['Hormonal changes', 'Nutritional deficiency', 'Stress', 'Rapid weight loss'], goals: ['Hair health support', 'Nutritional optimization', 'Hormonal balance'], path: 'Personalized optimization path' },
  { id: 'recovery-slow', label: 'Recovery Problems', causes: ['Declining growth hormone', 'Low NAD+', 'Poor sleep', 'Inflammation'], goals: ['Faster recovery', 'Better workout performance', 'Sustained activity'], path: 'Recovery Elite optimization path' },
  { id: 'low-libido', label: 'Low Libido', causes: ['Hormonal changes', 'Stress and fatigue', 'Neurological signal decline', 'Medication side effects'], goals: ['Restored desire', 'Improved intimacy', 'Overall wellness'], path: 'Personalized optimization path' },
  { id: 'muscle-loss', label: 'Muscle Loss', causes: ['Calorie restriction', 'Insufficient protein', 'Declining growth hormone', 'Low activity'], goals: ['Muscle preservation', 'Better body composition', 'Strength maintenance'], path: 'Body Composition optimization path' },
];

const STACKS = [
  { id: 'plateau', name: 'Plateau Breaker', bestFor: ['Weight-loss stalls', 'Metabolic slowdown', 'Body composition'], benefits: ['Improved momentum', 'Energy support', 'Body composition support'], why: "This is one of the most common patterns I see. The medication is still working — the body just needs additional support to keep responding.", color: 'from-emerald-500/10 border-emerald-500/20' },
  { id: 'energy', name: 'Energy Upgrade', bestFor: ['Fatigue', 'Low motivation', 'Brain fog'], benefits: ['Cellular energy support', 'Recovery support', 'Performance support'], why: "Most people assume they need more sleep. Often what they actually need is better cellular energy production.", color: 'from-cyan-500/10 border-cyan-500/20' },
  { id: 'women', name: "Women's Optimization", bestFor: ['Perimenopause', 'Menopause', 'Fatigue', 'Sleep issues', 'Mood changes'], benefits: ['Hormone support', 'Energy support', 'Body composition support', 'Sleep support'], why: "Women over 40 are dealing with multiple systems shifting at once. One-size-fits-all doesn't work here.", color: 'from-rose-500/10 border-rose-500/20' },
  { id: 'recovery', name: 'Recovery Elite', bestFor: ['Joint discomfort', 'Exercise recovery', 'Active lifestyles', 'Gut support'], benefits: ['Recovery support', 'Joint support', 'Digestive support'], why: "Getting healthier often means getting more active — and that's when old injuries and recovery issues show up.", color: 'from-orange-500/10 border-orange-500/20' },
  { id: 'aging', name: 'Healthy Aging', bestFor: ['Longevity', 'Recovery', 'Performance'], benefits: ['Energy', 'Recovery', 'Healthy aging support'], why: "The goal isn't to turn back the clock. It's to feel like the best version of where you are right now.", color: 'from-purple-500/10 border-purple-500/20' },
];

const STORIES = [
  { name: 'Sarah, 47', story: 'Struggled with energy and menopause symptoms. Built a personalized optimization plan. Started feeling more like herself again.' },
  { name: 'Tyler, 42', story: 'Weight loss slowed after initial success. Focused on body composition and recovery. Regained momentum.' },
  { name: 'Dianne, 51', story: 'Wanted support beyond weight loss. Focused on energy and healthy aging. Improved consistency and confidence.' },
];

const PATHS_TABLE = [
  { goal: 'Weight Loss', path: 'GLP-1 Optimization' },
  { goal: 'Low Energy', path: 'Energy Optimization' },
  { goal: 'Recovery', path: 'Recovery Optimization' },
  { goal: 'Menopause', path: "Women's Optimization" },
  { goal: 'Joint Pain', path: 'Recovery Optimization' },
  { goal: 'Brain Fog', path: 'Energy Optimization' },
  { goal: 'Body Composition', path: 'Growth Hormone Optimization' },
  { goal: 'Healthy Aging', path: 'Longevity Optimization' },
];

const OptimizePage = () => {
  const navigate = useNavigate();
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [expandedSymptom, setExpandedSymptom] = useState(null);
  const [askInput, setAskInput] = useState('');
  const [askResponse, setAskResponse] = useState('');
  const [askLoading, setAskLoading] = useState(false);

  const toggleGoal = (id) => {
    setSelectedGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const handleAsk = async () => {
    if (!askInput.trim() || askLoading) return;
    setAskLoading(true);
    setAskResponse('');
    try {
      const res = await axios.post(`${API}/chat`, {
        message: `I'm looking for health optimization guidance. Here's my situation: ${askInput}`,
        conversation_history: [],
      });
      setAskResponse(res.data.response);
    } catch {
      setAskResponse("I'd love to help with that. The best next step is to build your free AI forecast — it takes about 3 minutes and gives you a personalized recommendation. Or you can text Bryan directly at 801-651-8363.");
    } finally {
      setAskLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      <Helmet>
        <title>Optimize Your Health | Bryan 2.0 - AI Health Optimization</title>
        <meta name="description" content="Meet Bryan 2.0. Tell me what's holding you back and I'll help build your personalized optimization plan." />
      </Helmet>

      {/* Nav */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center">
              <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
            </div>
            <span className="font-heading text-xl font-bold text-white">Bryan 2.0</span>
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6">
            <Link to="/stack-finder" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
              <Search className="h-4 w-4" /><span className="hidden sm:inline">Stack Finder</span>
            </Link>
            <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
              <BookOpen className="h-4 w-4" /><span className="hidden sm:inline">Blog</span>
            </Link>
            <a href="sms:8016518363" className="text-slate-400 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /><span>Text Bryan</span>
            </a>
            <Button onClick={() => navigate('/forecast')} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-6 py-2 text-sm font-bold transition-all" data-testid="nav-cta">
              AI Forecast
            </Button>
          </nav>
        </div>
      </header>

      {/* S1: Hero */}
      <section className="relative overflow-hidden pt-20 pb-16">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-cyan-500 opacity-[0.07] blur-[120px] rounded-full" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="optimize-hero-title">
              Meet <span className="text-cyan-400">Bryan 2.0</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              I've helped thousands of patients improve weight loss, energy, recovery, hormones, sleep, gut health, and healthy aging. Tell me what's holding you back and I'll help build your personalized optimization plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/forecast')} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-10 py-7 text-lg font-bold shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all hover:scale-105 h-auto" data-testid="hero-cta-plan">
                <Sparkles className="mr-2 h-5 w-5" /> Build My Personalized Plan
              </Button>
              <Button onClick={() => navigate('/forecast')} variant="outline" className="border-2 border-white/15 hover:border-white/30 bg-white/5 text-white rounded-full px-10 py-7 text-lg font-bold h-auto" data-testid="hero-cta-forecast">
                Take My Free AI Forecast
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* S2: Goal Selection */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-3" data-testid="goals-section">What Would You Like To Improve?</h2>
          <p className="text-slate-500 text-center mb-10">Select all that apply</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {GOALS.map(g => {
              const Icon = g.icon;
              const selected = selectedGoals.includes(g.id);
              return (
                <button key={g.id} onClick={() => toggleGoal(g.id)} className={`relative p-4 rounded-xl border text-left transition-all ${selected ? 'border-cyan-500 bg-cyan-500/10 scale-[1.02]' : 'border-white/10 bg-slate-950/40 hover:border-white/20'}`} data-testid={`goal-${g.id}`}>
                  <Icon className={`h-6 w-6 mb-2 ${selected ? 'text-cyan-400' : 'text-slate-500'}`} strokeWidth={1.5} />
                  <span className={`text-sm font-medium ${selected ? 'text-white' : 'text-slate-300'}`}>{g.label}</span>
                  {selected && <Check className="absolute top-3 right-3 h-4 w-4 text-cyan-400" />}
                </button>
              );
            })}
          </div>
          {selectedGoals.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center mt-8">
              <Button onClick={() => navigate(`/forecast?goal=${selectedGoals[0]}`)} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full px-8 py-4 font-bold transition-all" data-testid="goals-build-plan">
                Build My Plan Based on {selectedGoals.length} Goal{selectedGoals.length > 1 ? 's' : ''} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* S3: Symptom Cards */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-3">What's Slowing You Down Right Now?</h2>
          <p className="text-slate-500 text-center mb-10">Click to explore common causes and optimization pathways</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {SYMPTOMS.map(s => (
              <div key={s.id}>
                <button onClick={() => setExpandedSymptom(expandedSymptom === s.id ? null : s.id)} className={`w-full p-4 rounded-xl border text-left transition-all ${expandedSymptom === s.id ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-white/10 bg-slate-950/40 hover:border-white/20'}`} data-testid={`symptom-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-300">{s.label}</span>
                    {expandedSymptom === s.id ? <ChevronUp className="h-4 w-4 text-cyan-400" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                  </div>
                </button>
                <AnimatePresence>
                  {expandedSymptom === s.id && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="p-4 mt-2 rounded-xl bg-slate-900/60 border border-white/10 text-sm space-y-3">
                        <div><span className="text-slate-500 text-xs uppercase tracking-wider">Common Causes</span>
                          <ul className="mt-1 space-y-1">{s.causes.map((c, i) => <li key={i} className="text-slate-400">• {c}</li>)}</ul>
                        </div>
                        <div><span className="text-slate-500 text-xs uppercase tracking-wider">Patient Goals</span>
                          <ul className="mt-1 space-y-1">{s.goals.map((g, i) => <li key={i} className="text-slate-300">• {g}</li>)}</ul>
                        </div>
                        <div className="text-cyan-400 text-xs font-medium">{s.path}</div>
                        <Button onClick={() => navigate('/forecast')} size="sm" className="w-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 rounded-lg text-xs">Build My Plan</Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* S4: Bryan's Favorite Stacks */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-3">Bryan's Favorite Optimization Paths</h2>
          <p className="text-slate-500 text-center mb-10">Curated protocols built around specific goals</p>
          <div className="space-y-4">
            {STACKS.map((s, idx) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }} className={`bg-gradient-to-br ${s.color} border rounded-2xl p-6 sm:p-8`} data-testid={`stack-${s.id}`}>
                <h3 className="font-heading text-2xl font-bold text-white mb-4">{s.name}</h3>
                <div className="grid sm:grid-cols-3 gap-6 mb-4 text-sm">
                  <div>
                    <span className="text-slate-500 block mb-2 text-xs uppercase tracking-wider">Best For</span>
                    {s.bestFor.map((b, i) => <span key={i} className="block text-slate-300">• {b}</span>)}
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-2 text-xs uppercase tracking-wider">Benefits</span>
                    {s.benefits.map((b, i) => <span key={i} className="block text-slate-300">• {b}</span>)}
                  </div>
                  <div>
                    <span className="text-slate-500 block mb-2 text-xs uppercase tracking-wider">Why Bryan Likes It</span>
                    <p className="text-slate-400 italic text-sm">"{s.why}"</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/forecast')} className="bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-full px-6 py-3 text-sm font-bold">Build My Plan <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* S5: Recovery & Gut Health */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-3">Why Recovery Matters</h2>
          <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">Many patients focus only on weight loss while overlooking recovery, gut health, inflammation, and overall resilience.</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* BPC-157 */}
            <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-2xl p-6" data-testid="recovery-bpc157">
              <h3 className="font-heading text-xl font-bold text-white mb-4">BPC-157</h3>
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Best For</p>
              <div className="space-y-1 mb-4 text-sm">
                {['Gut Health', 'Digestive Support', 'Joint Recovery', 'Tendon Support', 'Exercise Recovery', 'Active Lifestyles'].map(b => (
                  <span key={b} className="flex items-center gap-2 text-slate-300"><Check className="h-3 w-3 text-orange-400" /> {b}</span>
                ))}
              </div>
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Why Patients Explore It</p>
              <p className="text-slate-400 text-sm">Targeted recovery support for a specific area of concern. Often explored by people dealing with one main trouble spot — a joint, tendon, or digestive issue.</p>
            </div>
            {/* Wolverine */}
            <div className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-2xl p-6" data-testid="recovery-wolverine">
              <h3 className="font-heading text-xl font-bold text-white mb-4">Wolverine Blend</h3>
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Best For</p>
              <div className="space-y-1 mb-4 text-sm">
                {['Advanced Recovery', 'Joint Support', 'Gut Support', 'Active Lifestyle', 'Exercise Recovery'].map(b => (
                  <span key={b} className="flex items-center gap-2 text-slate-300"><Check className="h-3 w-3 text-amber-400" /> {b}</span>
                ))}
              </div>
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Why Patients Explore It</p>
              <p className="text-slate-400 text-sm">A comprehensive BPC-157 + TB-500 blend for broader, multi-area recovery. Best for people with multiple recovery concerns or ongoing athletic demands.</p>
            </div>
          </div>

          {/* Comparison */}
          <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-6 mb-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-white/10">
                <th className="pb-3 text-slate-500 font-medium"></th>
                <th className="pb-3 text-orange-400 font-medium">BPC-157</th>
                <th className="pb-3 text-amber-400 font-medium">Wolverine Blend</th>
              </tr></thead>
              <tbody className="text-slate-300">
                <tr className="border-b border-white/5"><td className="py-3 text-slate-500">Approach</td><td className="py-3">Targeted / localized</td><td className="py-3">Comprehensive / multi-area</td></tr>
                <tr className="border-b border-white/5"><td className="py-3 text-slate-500">Best for</td><td className="py-3">One main trouble spot</td><td className="py-3">Multiple areas or ongoing recovery</td></tr>
                <tr className="border-b border-white/5"><td className="py-3 text-slate-500">Gut support</td><td className="py-3"><Check className="h-4 w-4 text-orange-400" /></td><td className="py-3"><Check className="h-4 w-4 text-amber-400" /></td></tr>
                <tr className="border-b border-white/5"><td className="py-3 text-slate-500">Joint support</td><td className="py-3"><Check className="h-4 w-4 text-orange-400" /></td><td className="py-3"><Check className="h-4 w-4 text-amber-400" /></td></tr>
                <tr><td className="py-3 text-slate-500">Systemic recovery</td><td className="py-3 text-slate-500">—</td><td className="py-3"><Check className="h-4 w-4 text-amber-400" /></td></tr>
              </tbody>
            </table>
          </div>
          <div className="text-center">
            <Button onClick={() => navigate('/forecast?goal=recovery')} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white rounded-full px-8 py-4 font-bold">Find My Recovery Protocol <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
      </section>

      {/* S6: Women's Hormone Optimization */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-rose-500/10 to-pink-500/5 border border-rose-500/20 rounded-2xl p-8 sm:p-12" data-testid="womens-section">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Feel Like Yourself Again</h2>
            <p className="text-slate-400 mb-8 max-w-xl">Many women struggle with fatigue, weight gain, poor sleep, brain fog, and changing hormones long before they realize hormone optimization may be worth exploring.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-8">
              {['Fatigue', 'Brain Fog', 'Weight Gain', 'Mood Changes', 'Poor Sleep', 'Menopause', 'Perimenopause', 'Low Libido', 'Recovery Issues'].map(c => (
                <span key={c} className="flex items-center gap-2 text-sm text-slate-300"><Check className="h-3 w-3 text-rose-400 shrink-0" /> {c}</span>
              ))}
            </div>
            <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Benefits</p>
            <div className="flex flex-wrap gap-3 mb-8">
              {['Energy support', 'Sleep support', 'Hormone support', 'Body composition support', 'Overall wellness support'].map(b => (
                <span key={b} className="px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">{b}</span>
              ))}
            </div>
            <Button onClick={() => navigate('/forecast?goal=hormones')} className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white rounded-full px-8 py-4 font-bold" data-testid="hormone-cta">
              Take The Hormone Assessment <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* S7: Why Clients Choose Bryan */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-10">Why Clients Choose Bryan</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, label: '500+ Clients Guided', sub: 'Personalized recommendations' },
              { icon: Star, label: '5-Star Rating', sub: 'Consistent client satisfaction' },
              { icon: MessageSquare, label: 'Real Human Guidance', sub: 'Personal responses — not scripts' },
              { icon: Target, label: 'No One-Size-Fits-All', sub: 'Thousands of patient conversations' },
            ].map((t, i) => (
              <div key={i} className="bg-slate-950/60 border border-white/10 rounded-xl p-5 text-center">
                <t.icon className="h-7 w-7 text-cyan-400 mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-bold text-white text-sm mb-1">{t.label}</p>
                <p className="text-slate-500 text-xs">{t.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* S8: Real Client Stories */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-10">Real Client Stories</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {STORIES.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-slate-950/60 border border-white/10 rounded-2xl p-6">
                <p className="font-heading text-lg font-bold text-cyan-400 mb-3">{s.name}</p>
                <p className="text-slate-400 text-sm leading-relaxed">{s.story}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-600 mt-6">Names changed for privacy. Results vary. Not medical advice.</p>
        </div>
      </section>

      {/* S9: Compare Paths */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center mb-10">Compare Optimization Paths</h2>
          <div className="bg-slate-950/60 border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 bg-white/[0.02]">
                <th className="px-6 py-4 text-left text-slate-500 font-medium">Goal</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-medium">Common Starting Point</th>
              </tr></thead>
              <tbody>
                {PATHS_TABLE.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-4 text-slate-300">{r.goal}</td>
                    <td className="px-6 py-4 text-slate-400">{r.path}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* S10: Ask Bryan 2.0 */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-2xl p-8" data-testid="ask-bryan-section">
            <h2 className="font-heading text-3xl font-bold text-center mb-3">Ask Bryan 2.0</h2>
            <p className="text-slate-400 text-center mb-6">Tell me your age, goals, and biggest frustrations.</p>
            <div className="bg-slate-950/60 border border-white/10 rounded-xl p-4 mb-4">
              <textarea
                value={askInput}
                onChange={e => setAskInput(e.target.value)}
                placeholder="I'm 46, exhausted, sleeping poorly, and my weight loss has stalled."
                className="w-full bg-transparent text-white placeholder-slate-500 text-sm outline-none resize-none h-24"
                data-testid="ask-input"
              />
              <div className="flex justify-end">
                <Button onClick={handleAsk} disabled={!askInput.trim() || askLoading} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full px-6 py-2 text-sm font-bold disabled:opacity-30" data-testid="ask-submit">
                  {askLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Ask Bryan</>}
                </Button>
              </div>
            </div>
            {askResponse && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900/60 border border-white/10 rounded-xl p-4 mb-4">
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{askResponse}</p>
              </motion.div>
            )}
            <div className="text-center">
              <Button onClick={() => navigate('/forecast')} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full px-8 py-4 font-bold mt-2">
                <Sparkles className="mr-2 h-5 w-5" /> Build My Personalized Plan
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Not Sure Where To Start?</h2>
          <p className="text-slate-400 text-lg mb-8">Take the free 2-minute AI Forecast and Bryan will help identify the areas most likely to move the needle for your goals.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/forecast')} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-10 py-7 text-lg font-bold shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all h-auto" data-testid="final-cta-forecast">
              <Sparkles className="mr-2 h-5 w-5" /> Get My Free AI Forecast
            </Button>
            <a href="sms:8016518363" className="inline-flex items-center justify-center border-2 border-white/15 hover:border-white/30 bg-white/5 text-white rounded-full px-10 py-7 text-lg font-bold transition-all h-auto" data-testid="final-cta-text">
              <MessageSquare className="mr-2 h-5 w-5" /> Text Bryan Directly
            </a>
          </div>
          <p className="text-slate-600 text-xs mt-6">Results vary. All programs include licensed medical provider oversight. Not medical advice.</p>
        </div>
      </section>
    </div>
  );
};

export default OptimizePage;
